import React, { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Loader2 } from 'lucide-react';
import { getMonitoringStatus, getConfiguration } from '../services/api';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';

function SettingsPage() {
  const [config, setConfig] = useState(null);
  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [configRes, statusRes] = await Promise.all([
        getConfiguration(),
        getMonitoringStatus(),
      ]);
      setConfig(configRes.data.data);
      setMonitoringStatus(statusRes.data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="mx-auto flex max-w-[1400px] flex-col gap-6">
      <PageHeader
        title="Settings"
        description="Configure monitoring parameters and view system status"
        actions={
          <Button variant="outline" onClick={loadSettings}>
            <RefreshCw />
            Refresh
          </Button>
        }
      />

      {/* Monitoring Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Monitoring Status</CardTitle>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          <div className="mb-4 flex items-center gap-3">
            <span className="text-sm text-foreground">Service Status:</span>
            <Badge variant={monitoringStatus?.running ? 'success' : 'destructive'}>
              {monitoringStatus?.running ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              {monitoringStatus?.running ? 'Running' : 'Stopped'}
            </Badge>
          </div>

          <div className="mb-2 text-sm font-medium text-foreground">Active Monitors:</div>
          <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
            {monitoringStatus?.monitors &&
              Object.entries(monitoringStatus.monitors).map(([name, status]) => {
                const active = status.enabled && status.running;
                return (
                  <Badge
                    key={name}
                    variant={active ? 'success' : status.enabled ? 'secondary' : 'outline'}
                    className="justify-start capitalize"
                  >
                    {active ? (
                      <CheckCircle2 className="h-3 w-3" />
                    ) : (
                      <XCircle className="h-3 w-3" />
                    )}
                    {name}
                  </Badge>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Monitoring Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Monitoring Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          {config?.monitoring && (
            <div className="flex flex-col gap-4">
              <div>
                <div className="mb-2 text-sm font-medium text-foreground">
                  Screenshot Monitoring
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  <p className="text-sm text-muted-foreground">
                    Interval: {config.monitoring.screenshot.interval} seconds
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Quality: {config.monitoring.screenshot.quality}%
                  </p>
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-foreground">
                  Clipboard Monitoring
                </div>
                <p className="text-sm text-muted-foreground">
                  Check Interval: {config.monitoring.clipboard.check_interval} seconds
                </p>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-foreground">
                  Application Monitoring
                </div>
                <p className="text-sm text-muted-foreground">
                  Check Interval: {config.monitoring.application.check_interval} seconds
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storage Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Storage Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <Separator className="mb-4" />
          {config?.storage && (
            <div className="flex flex-col gap-1.5">
              <p className="text-sm text-muted-foreground">
                Max Storage: {config.storage.max_storage_mb} MB
              </p>
              <p className="text-sm text-muted-foreground">
                Retention: {config.storage.retention_days} days
              </p>
              <p className="text-sm text-muted-foreground">
                Auto Cleanup: {config.storage.auto_cleanup ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert variant="info">
        <AlertDescription>
          <strong>Note:</strong> Configuration changes require backend service restart to take
          effect. This is an MVP version - full configuration management will be added in future
          updates.
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default SettingsPage;
